import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "../../../../components/common/Toast/toast";
import { deleteBatch } from "../../../../../services/BatchService.js";
import { getBookingsList } from "../../../../../services/ClassroomManagementService.js";
import { useAuth } from "../../../../../context/AuthContext.js";
import NewCustomDataTable from "../../../../components/common/NewCustomDataTable.jsx";
import useDebouncedCallback from "../../../../components/common/useDebouncedCallback";
import useScrollLoadMore from "../../../../components/common/useScrollLoadMore";
import BookingFormModal from "./BookingFormModal.jsx";
import PopupDone, { usePopupDone } from "../../../../components/common/PopupDone/PopupDone.jsx";
/* The page kit, in place of the classroom app's own cls-* vocabulary --
   see LabsTab for the full note. classroomApp.css is left alone; the
   tabs still on it need it. */
import "../../../../components/Trainer/TrainerDashboard.css";
import "../../../../components/common/lms-pages.css";
import { confirm as confirmDialog } from "../../../../components/common/Confirm/confirm";

const PAGE_SIZE = 25;
const PAGE_SIZE_CHOICES = [25, 50, 100];
const DASH = "—";

const timeLabel = (iso) =>
  iso ? new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : DASH;
const dateLabel = (iso) => (iso ? new Date(iso).toLocaleDateString() : DASH);

/* "Monday,Wednesday" is three words of column for two days of
   information. */
const daysLabel = (days) =>
  String(days || "")
    .split(",")
    .map((d) => d.trim().slice(0, 3))
    .filter(Boolean)
    .join(" · ");

const BookingsTab = () => {
  const { hasPermission } = useAuth();
  const canAdd = hasPermission("list-classroom", ["add"], "lms");
  const canEdit = hasPermission("list-classroom", ["update"], "lms");
  const canDelete = hasPermission("list-classroom", ["delete"], "lms");

  const [bookings, setBookings] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  /* A scroll batch, as against a fresh load. The table veils itself for
     `loading` -- right for a first load, wrong for adding rows under
     something being read. */
  const [loadingMore, setLoadingMore] = useState(false);
  const [uiSearchTerm, setUiSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE);

  const [details, setDetails] = useState(null);
  const [formModal, setFormModal] = useState(null);
  // A delete from the view popup is confirmed in it: the done panel
  // replaces its body and it closes itself after five seconds.
  const { done } = usePopupDone(Boolean(details));

  // Read before the fetch awaits anything: a reset mid-flight would turn
  // an append into a replace and snap the list back to its first page.
  const appendRef = useRef(false);
  const listEndRef = useRef(null);

  const load = useCallback(async () => {
    const isAppend = appendRef.current;
    if (isAppend) setLoadingMore(true);
    else setLoading(true);
    try {
      const result = await getBookingsList(page, pageSize, search);
      const rows = result.data || [];
      setBookings((prev) => {
        if (!isAppend) return rows;
        /* The same row can arrive twice if one is added server-side
           between two batches and pushes the page boundary along. */
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...rows.filter((r) => !seen.has(r.id))];
      });
      setPagination(result.pagination);
    } catch (error) {
      console.error("Error loading bookings:", error);
      toast.error("Failed to load bookings");
    } finally {
      appendRef.current = false;
      setLoading(false);
      setLoadingMore(false);
    }
  }, [page, pageSize, search]);

  useEffect(() => {
    load();
  }, [load]);

  /* Typing searches; it does not wait for a button, and it does not fire
     a request per keystroke. A new search is a fresh list from page 1. */
  const runSearch = useDebouncedCallback((value) => {
    appendRef.current = false;
    setSearch(value.trim());
    setPage(1);
  });

  const totalRecords = pagination?.total ?? bookings.length;
  const totalPages = pagination?.totalPages ?? 1;
  const hasMore = page < totalPages;

  /* The page is the scroller, so the foot of the list reaching the
     viewport asks for the next batch -- the /list-attendance behaviour.
     Declared below `hasMore`, which it reads, and reconnected on
     loadingMore, which moves on every batch (list_page_rules in
     instruction_ui.json). */
  useScrollLoadMore(
    listEndRef,
    () => {
      if (loading || loadingMore || !hasMore) return;
      appendRef.current = true;
      setPage((p) => p + 1);
    },
    [bookings.length, page, totalPages, loading, loadingMore],
  );

  /* After a save or a delete the list is rebuilt from page 1, so a new
     booking appears and a removed one goes. On page 1 that is a refetch in
     place; further down, moving back to page 1 is what triggers it --
     calling load() there would replace the list with that page alone. */
  const refresh = useCallback(() => {
    appendRef.current = false;
    if (page === 1) load();
    else setPage(1);
  }, [page, load]);

  const handleDelete = async (booking) => {
    const confirm = await confirmDialog({
      title: "Delete Confirmation",
      text: `Are you sure you want to delete the booking “${booking.name}”?`,
      confirmButtonText: "Yes, Delete it!",
      tone: 'danger',
    });
    if (!confirm.isConfirmed) return;
    try {
      await deleteBatch(booking.id);
      // The view popup shows the done panel and closes itself after it.
      toast.success("Booking deleted");
      refresh();
    } catch (error) {
      toast.error("Failed to delete booking");
    }
  };

  /* Five columns. The course, the dates it runs between and both actions
     are in the dialog a row opens, which is also what lets a phone keep
     the batch and the room and drop the rest. */
  const headers = useMemo(
    () => [
      {
        title: "Batch",
        prop: "name",
        isSortable: true,
        cell: (row) => <span className="pg-strong">{row.name || DASH}</span>,
      },
      {
        title: "Trainer",
        prop: "trainerName",
        isSortable: true,
        cell: (row) =>
          row.trainerName ? (
            <span>{row.trainerName}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Room",
        prop: "classroomNo",
        isSortable: true,
        cell: (row) =>
          row.classroomNo ? (
            <span>{row.classroomNo}</span>
          ) : (
            <span className="pg-none">{DASH}</span>
          ),
      },
      {
        title: "Days",
        prop: "days",
        cell: (row) => {
          const label = daysLabel(row.days);
          return label ? <span>{label}</span> : <span className="pg-none">{DASH}</span>;
        },
      },
      {
        title: "Time",
        prop: "startTime",
        isSortable: true,
        cell: (row) => (
          <span className="pg-date">
            {timeLabel(row.startTime)} – {timeLabel(row.endTime)}
          </span>
        ),
      },
    ],
    [],
  );

  return (
    <div className="pg">
      <header className="pg-head">
        <div className="pg-head-main">
          <h1 className="pg-title">Bookings</h1>
          <p className="pg-sub">
            Which batch is in which room, on which days, and at what time.
          </p>
        </div>

        {canAdd && (
          <div className="pg-head-actions">
            <button
              type="button"
              className="pg-btn pg-btn--main"
              onClick={() => setFormModal({ booking: null })}
            >
              New booking
            </button>
          </div>
        )}
      </header>

      <section className="pg-filters" aria-label="Filter bookings">
        <div className="pg-filters-search">
          <div className="pg-field pg-field--search">
            <label className="pg-label" htmlFor="booking-search">
              Search
            </label>
            <input
              id="booking-search"
              type="search"
              className="pg-input pg-input--search"
              placeholder="Search by batch name..."
              value={uiSearchTerm}
              onChange={(e) => {
                setUiSearchTerm(e.target.value);
                runSearch(e.target.value);
              }}
            />
          </div>

          <div className="pg-field pg-field--tight">
            <label className="pg-label" htmlFor="booking-rows">
              Rows per load
            </label>
            <select
              id="booking-rows"
              className="pg-select"
              value={pageSize}
              onChange={(e) => {
                appendRef.current = false;
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {PAGE_SIZE_CHOICES.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </div>
        </div>
      </section>

      {!loading && bookings.length === 0 ? (
        <div className="pg-state">
          <h3 className="pg-state-title">
            {search ? "Nothing matched" : "No bookings yet"}
          </h3>
          <p className="pg-state-text">
            {search
              ? `No booking matches “${search}”.`
              : "A booking puts a batch in a room for a run of dates."}
          </p>
          {search && (
            <button
              type="button"
              className="pg-btn"
              onClick={() => {
                setUiSearchTerm("");
                runSearch.flush("");
              }}
            >
              Clear search
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="td-list td-list--bookings">
            <NewCustomDataTable
              data={bookings}
              headers={headers}
              totalRecords={totalRecords}
              currentPage={page}
              recordsPerPage={pageSize}
              serverSide
              loading={loading}
              loadingMore={loadingMore}
              showToolbar={false}
              showPagination={false}
              showFooter={false}
              onRowClick={(row) => setDetails(row)}
              validRowClickTagNames="*"
              rowProps={(row) => ({
                tabIndex: 0,
                role: "button",
                "aria-label": `Open ${row.name || "this booking"}`,
                onKeyDown: (e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.preventDefault();
                    setDetails(row);
                  }
                },
              })}
            />
          </div>

          {/* What the pager used to say, said once: how far down the list
              the reader is. It is also the sentinel the observer watches --
              crossing it fetches the next batch. */}
          <div className="pg-scroll-foot" ref={listEndRef}>
            {bookings.length < totalRecords ? (
              <>
                <span className="pg-scroll-count">
                  {bookings.length} of {totalRecords}
                </span>
                <span
                  className={`pg-spinner${loadingMore ? "" : " pg-spinner--slot"}`}
                  aria-hidden="true"
                />
              </>
            ) : (
              totalRecords > 0 && (
                <span className="pg-scroll-count">
                  All {totalRecords} booking{totalRecords === 1 ? "" : "s"} loaded
                </span>
              )
            )}
          </div>
        </>
      )}

      {details && (
        <div
          className="pg-modal"
          role="dialog"
          aria-modal="true"
          aria-labelledby="booking-detail-title"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setDetails(null);
          }}
        >
          <div className="pg-modal-panel pg-modal-panel--wide">
            <header className="pg-modal-head">
              <div className="pg-modal-heading">
                <span className="pg-modal-eyebrow">Booking</span>
                <h3 className="pg-modal-title" id="booking-detail-title">
                  {details.name || "Unnamed batch"}
                </h3>
                {details.courseName && (
                  <p className="pg-modal-sub">{details.courseName}</p>
                )}
              </div>
              <button
                type="button"
                className="pg-modal-close"
                onClick={() => setDetails(null)}
                aria-label="Close"
              >
                &times;
              </button>
            </header>

            <div className="pg-modal-body">
              {done ? (
                <PopupDone message={done} onClose={() => setDetails(null)} />
              ) : (
                <dl className="quiz-facts">
                  <div>
                    <dt>Course</dt>
                    <dd>{details.courseName || DASH}</dd>
                  </div>
                  <div>
                    <dt>Trainer</dt>
                    <dd>{details.trainerName || DASH}</dd>
                  </div>
                  <div>
                    <dt>Room</dt>
                    <dd>{details.classroomNo || DASH}</dd>
                  </div>
                  <div>
                    <dt>Days</dt>
                    <dd>{daysLabel(details.days) || DASH}</dd>
                  </div>
                  <div>
                    <dt>Time</dt>
                    <dd>
                      {timeLabel(details.startTime)} – {timeLabel(details.endTime)}
                    </dd>
                  </div>
                  <div>
                    <dt>Runs</dt>
                    <dd>
                      {dateLabel(details.startTime)} → {dateLabel(details.endTime)}
                    </dd>
                  </div>
                </dl>
              )}
            </div>

            {!done && (
              <footer className="pg-modal-foot">
                <div className="pgm-actions">
                  {canEdit && (
                    <button
                      type="button"
                      className="pgm-btn"
                      onClick={() => {
                        const booking = details;
                        setFormModal({ booking });
                      }}
                    >
                      Edit booking
                    </button>
                  )}
                  {canDelete && (
                    <button
                      type="button"
                      className="pgm-btn pgm-btn--danger"
                      onClick={() => {
                        const booking = details;
                        handleDelete(booking);
                      }}
                    >
                      Delete booking
                    </button>
                  )}
                </div>
                <button
                  type="button"
                  className="pg-btn pg-btn--main"
                  onClick={() => setDetails(null)}
                >
                  Close
                </button>
              </footer>
            )}
          </div>
        </div>
      )}

      {formModal && (
        <BookingFormModal
          booking={formModal.booking}
          onClose={() => setFormModal(null)}
          onSaved={() => {
            // Saved: the form shows its done panel and closes itself; the
            // view popup behind it, which would show the old record, closes
            // now (the form is its sibling, not its child).
            setDetails(null);
            refresh();
          }}
        />
      )}
    </div>
  );
};

export default BookingsTab;
